import React, { useState, useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Sector,
  SectorProps,
} from "recharts";

import "../../../styles/charts/AssetsCategoryPieChart.scss";
import CommonSelect from "../../../../../../core/common/commonSelect";

interface CategoryData {
  category: string;
  categoryId?: number;
  total: number;
  assigned: number;
  instock: number;
  inactive: number;
  child: CategoryData[];
  count?: number;
}

interface PieDataPoint {
  name: string;
  categoryId: number | null;
  value: number;
  type: string;
  color: string;
  hasChildren: boolean;
  originalData: CategoryData;
}

interface AssetCategoryPieChartProps {
  data: CategoryData[];
}

// Type definitions for active shape
type Coordinate = {
  x: number;
  y: number;
};

type PieSectorData = {
  percent?: number;
  name?: string | number;
  midAngle?: number;
  middleRadius?: number;
  tooltipPosition?: Coordinate;
  value?: number;
  paddingAngle?: number;
  dataKey?: string;
  payload?: any;
};

type PieSectorDataItem = React.SVGProps<SVGPathElement> &
  Partial<SectorProps> &
  PieSectorData;

const AssetCategoryPieChart: React.FC<AssetCategoryPieChartProps> = ({
  data,
}) => {
  const [selectedMetric, setSelectedMetric] = useState<string>("total");
  const [activeIndex, setActiveIndex] = useState<number | any>(0);
  const [navigationStack, setNavigationStack] = useState<
    Array<{
      categoryId: number | null;
      categoryName: string;
      data: CategoryData[];
    }>
  >([]);

  // Colors for different metrics
  const colors = {
    total: [
      "#8b5cf6",
      "#a78bfa",
      "#c4b5fd",
      "#ddd6fe",
      "#ede9fe",
      "#f3f0ff",
      "#e5dbff",
      "#d1c7fe",
    ],
    assigned: [
      "#3b82f6",
      "#60a5fa",
      "#93c5fd",
      "#bfdbfe",
      "#dbeafe",
      "#e0f2fe",
      "#bae6fd",
      "#7dd3fc",
    ],
    instock: [
      "#10b981",
      "#34d399",
      "#6ee7b7",
      "#9decda",
      "#a7f3d0",
      "#c6f6d5",
      "#86efac",
      "#4ade80",
    ],
    inactive: [
      "#ef4444",
      "#f87171",
      "#fca5a5",
      "#fecaca",
      "#fee2e2",
      "#fef2f2",
      "#fecdd3",
      "#fda4af",
    ],
  };

  // Metric display names
  const metricDisplayNames = {
    total: "Total Assets",
    assigned: "Assigned",
    instock: "In Stock",
    inactive: "Inactive",
  };

  // Current level data based on navigation
  const currentLevelData = useMemo(() => {
    if (navigationStack.length === 0) {
      return data;
    }
    return navigationStack[navigationStack.length - 1].data;
  }, [data, navigationStack]);

  // Helper function to get numeric value with proper type narrowing
  const getNumericValue = (item: CategoryData, metric: string): number => {
    switch (metric) {
      case "total":
        return item.total || item.count || 0;
      case "assigned":
        return item.assigned || 0;
      case "instock":
        return item.instock || 0;
      case "inactive":
        return item.inactive || 0;
      default:
        return 0;
    }
  };

  const pieData = useMemo((): PieDataPoint[] => {
    const colorArray = colors[selectedMetric as keyof typeof colors];
    return currentLevelData
      .map((item, index) => {
        const value = getNumericValue(item, selectedMetric);
        return {
          name: item.category,
          categoryId: item.categoryId || null,
          value: value,
          type: selectedMetric,
          color: colorArray[index % colorArray.length],
          hasChildren: item.child && item.child.length > 0,
          originalData: item,
        };
      })
      .filter((item) => item.value > 0);
  }, [currentLevelData, selectedMetric]); // Ensure selectedMetric is in dependencies

  // Active shape renderer for hover effect
  const renderActiveShape = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  }: PieSectorDataItem) => {
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * (midAngle ?? 1));
    const cos = Math.cos(-RADIAN * (midAngle ?? 1));
    const sx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
    const sy = (cy ?? 0) + ((outerRadius ?? 0) + 10) * sin;
    const mx = (cx ?? 0) + ((outerRadius ?? 0) + 30) * cos;
    const my = (cy ?? 0) + ((outerRadius ?? 0) + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text
          x={cx}
          y={cy}
          dy={8}
          textAnchor="middle"
          fill="#333"
          fontSize={10}
          fontWeight="400">
          {payload?.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={(outerRadius ?? 0) + 6}
          outerRadius={(outerRadius ?? 0) + 10}
          fill={fill}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
          fontSize={11}
          fontWeight="400">
          {`${
            metricDisplayNames[
              selectedMetric as keyof typeof metricDisplayNames
            ]
          }: ${value}`}
        </text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#666"
          fontSize={11}>
          {`(${((percent ?? 1) * 100).toFixed(1)}%)`}
        </text>
      </g>
    );
  };

  const handlePieClick = (data: any, index: number) => {
    const clickedItem = pieData[index];

    if (clickedItem && clickedItem.hasChildren) {
      const categoryData = currentLevelData.find(
        (item) => item.category === clickedItem.name
      );
      if (categoryData && categoryData.child) {
        setNavigationStack((prev) => [
          ...prev,
          {
            categoryId: clickedItem.categoryId,
            categoryName: clickedItem.name,
            data: categoryData.child,
          },
        ]);
        setActiveIndex(0);
      }
    }
  };

  const handleMouseEnter = (data: any, index: number) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(0);
  };

  const handleNavigateBack = (index: number) => {
    if (index === 0) {
      setNavigationStack([]);
    } else {
      setNavigationStack((prev) => prev.slice(0, index));
    }
    setActiveIndex(0);
    setSelectedMetric("total");
  };

  const viewByOptions = [
    { value: "total", label: "Total Assets" },
    { value: "assigned", label: "Assigned" },
    { value: "instock", label: "In Stock" },
    { value: "inactive", label: "Inactive" },
  ];

  const renderLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-RADIAN * midAngle);
    const y = cy + radius * Math.sin(-RADIAN * midAngle);

    return (
      <text
        x={x}
        y={y}
        fill="black"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={10}
        fontWeight="bold">
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  return (
    <>
      {/* Header Controls */}

      <div className="metric-selector">
        <div className="d-flex gap-2 align-items-center">
          <label className="metric-label">View by:</label>

          <CommonSelect
            className="select small text-muted"
            options={viewByOptions}
            defaultValue={
              selectedMetric
                ? viewByOptions.find((vw: any) => vw.value === selectedMetric)
                : undefined
            }
            onChange={(selected: any) => {
              setSelectedMetric(selected.value);
              setActiveIndex(0);
            }}
            placeHolder={"View By"}
          />
        </div>

        <button
          className="btn btn-outline-secondary btn-sm py-2 d-flex align-items-center"
          onClick={() => handleNavigateBack(0)}
          disabled={navigationStack.length == 0}>
          <i className="ti ti-refresh me-1" />
          Back to Root
        </button>
      </div>

      {/* Chart */}
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={90}
              outerRadius={130}
              paddingAngle={3}
              dataKey="value"
              nameKey="category"
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              onClick={handlePieClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              label={renderLabel}
              labelLine={false}>
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke={entry.hasChildren ? "#ffffff" : "transparent"}
                  strokeWidth={entry.hasChildren ? 2 : 0}
                  style={{
                    cursor: entry.hasChildren ? "pointer" : "default",
                  }}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <div className="chart-legend">
          <div className="legend-items d-flex flex-start gap-1">
            {pieData.map((item, index) => (
              <div
                key={index}
                className={`legend-item ${
                  activeIndex === index ? "active" : ""
                }`}>
                <div
                  className="legend-dot"
                  style={{ backgroundColor: item.color }}
                />
                <span className="legend-text" title={item.name}>
                  {item.name.length > 15
                    ? `${item.name.substring(0, 12)}...`
                    : item.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default AssetCategoryPieChart;
